import React, { useState, useEffect } from 'react';
import { Palette, RotateCcw, Save, User, Globe } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import ConfirmModal from '../components/common/ConfirmModal';
import { useTheme } from '../context/ThemeContext';
import { useToastContext } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

const SystemSettings = () => {
  const { theme, updateTheme, resetTheme } = useTheme();
  const toast = useToastContext();
  const { permissions, user } = useAuth();

  const [colors, setColors] = useState(theme);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [themeScope, setThemeScope] = useState('user'); // 'user' or 'system'

  // Initialize colors when theme changes
  useEffect(() => {
    setColors(theme);
  }, [theme]);

  // Check if user has access (now allows all authenticated users for personal themes)
  const canAccessSettings = permissions.isSuperAdmin() || user;
  
  if (!canAccessSettings) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">Please log in to access theme settings.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const handleColorChange = (key, value) => {
    setColors(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateTheme(colors, themeScope);
      setSaving(false);
      if (result.success) {
        const scopeText = themeScope === 'system' ? 'system-wide theme' : 'personal theme';
        toast.success(`${scopeText.charAt(0).toUpperCase() + scopeText.slice(1)} updated successfully!`);
      } else {
        toast.error('Failed to update theme: ' + result.error);
      }
    } catch (error) {
      setSaving(false);
      toast.error('Failed to update theme: ' + error.message);
    }
  };

  const handleResetClick = () => {
    setShowResetModal(true);
  };

  const handleConfirmReset = async () => {
    try {
      const result = await resetTheme(themeScope);
      if (result.success) {
        setColors(theme);
        const scopeText = themeScope === 'system' ? 'system-wide theme' : 'personal theme';
        toast.success(`${scopeText.charAt(0).toUpperCase() + scopeText.slice(1)} reset successfully!`);
      } else {
        toast.error('Failed to reset theme');
      }
    } catch (error) {
      toast.error('Failed to reset theme: ' + error.message);
    }
    setShowResetModal(false);
  };

  const colorFields = [
    { key: 'primary', label: 'Primary Color', description: 'Main brand color for buttons and links' },
    { key: 'primaryHover', label: 'Primary Hover', description: 'Hover state for primary elements' },
    { key: 'secondary', label: 'Secondary Color', description: 'Secondary accent color' },
    { key: 'success', label: 'Success Color', description: 'Success messages and positive actions' },
    { key: 'danger', label: 'Danger Color', description: 'Error messages and destructive actions' },
    { key: 'text', label: 'Text Color', description: 'Main text color' },
    { key: 'textLight', label: 'Light Text', description: 'Secondary text color' },
    { key: 'background', label: 'Background', description: 'Page background color' },
    { key: 'buttonText', label: 'Button Text', description: 'Text color on buttons' },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Theme Settings</h1>
            <p className="text-gray-600 mt-1">
              {themeScope === 'system' 
                ? 'Configure system-wide theme (applies to all users)' 
                : 'Configure your personal theme preferences'
              }
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={handleResetClick}
              className="gap-2"
            >
              <RotateCcw className="h-5 w-5" />
              Reset
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              className="gap-2"
            >
              <Save className="h-5 w-5" />
              Save Changes
            </Button>
          </div>
        </div>

        {/* Theme Scope Selection */}
        <Card>
          <Card.Content>
            <div className="flex items-center gap-2 mb-4">
              <Globe className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">Theme Scope</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setThemeScope('user')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  themeScope === 'user'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <User className="h-6 w-6 text-primary-600" />
                  <div className="text-left">
                    <h4 className="font-semibold text-gray-900">Personal Theme</h4>
                    <p className="text-sm text-gray-600">Customize your own interface colors</p>
                  </div>
                </div>
              </button>
              
              {permissions.isSuperAdmin() && (
                <button
                  onClick={() => setThemeScope('system')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    themeScope === 'system'
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Globe className="h-6 w-6 text-primary-600" />
                    <div className="text-left">
                      <h4 className="font-semibold text-gray-900">System-Wide Theme</h4>
                      <p className="text-sm text-gray-600">Set default colors for all users</p>
                    </div>
                  </div>
                </button>
              )}
            </div>
            
            {!permissions.isSuperAdmin() && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> You can only customize your personal theme. 
                  System-wide theme changes require Super Administrator privileges.
                </p>
              </div>
            )}
          </Card.Content>
        </Card>

        {/* Theme Customization */}
        <Card>
          <Card.Content>
            <div className="flex items-center gap-2 mb-6">
              <Palette className="h-6 w-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-900">Theme Colors</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {colorFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.label}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={colors[field.key] || '#000000'}
                      onChange={(e) => handleColorChange(field.key, e.target.value)}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={colors[field.key] || ''}
                      onChange={(e) => handleColorChange(field.key, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="#000000"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>

        {/* Preview */}
        <Card>
          <Card.Content>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
            <div className="space-y-4">
              <div className="flex gap-2">
                <button
                  style={{ backgroundColor: colors.primary, color: colors.buttonText }}
                  className="px-4 py-2 rounded-lg font-medium"
                >
                  Primary Button
                </button>
                <button
                  style={{ backgroundColor: colors.success, color: colors.buttonText }}
                  className="px-4 py-2 rounded-lg font-medium"
                >
                  Success Button
                </button>
                <button
                  style={{ backgroundColor: colors.danger, color: colors.buttonText }}
                  className="px-4 py-2 rounded-lg font-medium"
                >
                  Danger Button
                </button>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg">
                <p style={{ color: colors.text }} className="font-medium mb-2">
                  Sample Text (Primary)
                </p>
                <p style={{ color: colors.textLight }} className="text-sm">
                  Sample Text (Secondary)
                </p>
              </div>
            </div>
          </Card.Content>
        </Card>

        {/* Reset Confirmation Modal */}
        <ConfirmModal
          isOpen={showResetModal}
          onClose={() => setShowResetModal(false)}
          onConfirm={handleConfirmReset}
          title={`Reset ${themeScope === 'system' ? 'System-Wide' : 'Personal'} Theme`}
          message={`Are you sure you want to reset the ${themeScope === 'system' ? 'system-wide' : 'personal'} theme to default teal colors? This will discard all ${themeScope === 'system' ? 'system' : 'your'} customizations.`}
          confirmText="Reset"
          cancelText="Cancel"
          variant="warning"
        />
      </div>
    </Layout>
  );
};

export default SystemSettings;
